import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

// material-ui
import { Box, Card, CardContent, Typography, Tabs, Tab, Button, Chip, Alert, Paper, Divider } from '@mui/material';

// icons
import { IconTestPipe, IconApi, IconBrandPython, IconCopy, IconCircleCheck } from '@tabler/icons-react';

// Python examples
const pythonExamples = {
    basic: {
        title: 'Basic Function',
        description: 'Function cơ bản với input/output đơn giản',
        code: `@deploy
def hello_world(name: str = "World"):
    """Simple greeting function"""
    return {
        "message": f"Hello, {name}!",
        "timestamp": datetime.now().isoformat()
    }`,
        testInput: `{
  "name": "Python Developer"
}`,
        expectedOutput: `{
  "message": "Hello, Python Developer!",
  "timestamp": "2024-07-17T10:30:00"
}`
    },

    calculation: {
        title: 'Business Logic',
        description: 'Function tính toán với business logic phức tạp',
        code: `@deploy
def calculate_discount(price: float, quantity: int, customer_tier: str = "bronze", promo_code: str = None):
    """Calculate discount based on multiple factors"""
    
    # Tier discounts
    tier_discounts = {
        "bronze": 0.05,
        "silver": 0.10,
        "gold": 0.15,
        "platinum": 0.20
    }
    
    base_discount = tier_discounts.get(customer_tier, 0)
    
    # Quantity discount
    if quantity >= 10:
        base_discount += 0.05
    elif quantity >= 5:
        base_discount += 0.02
    
    # Promo code
    if promo_code == "SUMMER20":
        base_discount += 0.20
    elif promo_code == "SAVE10":
        base_discount += 0.10
    
    # Calculate final price
    discount_amount = price * quantity * base_discount
    final_price = (price * quantity) - discount_amount
    
    return {
        "original_price": price * quantity,
        "discount_percentage": base_discount * 100,
        "discount_amount": round(discount_amount, 2),
        "final_price": round(final_price, 2)
    }`,
        testInput: `{
  "price": 100,
  "quantity": 10,
  "customer_tier": "gold",
  "promo_code": "SUMMER20"
}`,
        expectedOutput: `{
  "original_price": 1000,
  "discount_percentage": 40,
  "discount_amount": 400.00,
  "final_price": 600.00
}`
    },

    dataProcessing: {
        title: 'Data Processing',
        description: 'Function xử lý data với pandas',
        code: `@deploy(dependencies=["pandas", "numpy"])
def analyze_sales_data(data: list, metrics: list = ["total", "average"]):
    """Analyze sales data and return statistics"""
    import pandas as pd
    import numpy as np
    
    # Convert to DataFrame
    df = pd.DataFrame(data)
    
    results = {}
    
    if "total" in metrics:
        results["total_sales"] = float(df["amount"].sum())
    
    if "average" in metrics:
        results["average_sale"] = float(df["amount"].mean())
    
    if "top_products" in metrics:
        top_products = df.groupby("product")["amount"].sum().nlargest(5)
        results["top_products"] = top_products.to_dict()
    
    if "daily_trend" in metrics:
        df["date"] = pd.to_datetime(df["date"])
        daily_sales = df.groupby(df["date"].dt.date)["amount"].sum()
        results["daily_trend"] = {
            str(date): float(amount) 
            for date, amount in daily_sales.items()
        }
    
    return results`,
        testInput: `{
  "data": [
    {"date": "2024-07-15", "product": "Laptop", "amount": 1200},
    {"date": "2024-07-15", "product": "Mouse", "amount": 50},
    {"date": "2024-07-16", "product": "Laptop", "amount": 1200},
    {"date": "2024-07-16", "product": "Keyboard", "amount": 80}
  ],
  "metrics": ["total", "average", "top_products"]
}`,
        expectedOutput: `{
  "total_sales": 2530.0,
  "average_sale": 632.5,
  "top_products": {
    "Laptop": 2400.0,
    "Keyboard": 80.0,
    "Mouse": 50.0
  }
}`
    },

    apiIntegration: {
        title: 'API Integration',
        description: 'Function tích hợp với external API',
        code: `@deploy(dependencies=["requests"])
def fetch_weather(city: str, units: str = "metric"):
    """Fetch weather data from external API"""
    import requests
    import os
    
    api_key = os.environ.get("WEATHER_API_KEY")
    base_url = "https://api.openweathermap.org/data/2.5/weather"
    
    try:
        response = requests.get(base_url, params={
            "q": city,
            "units": units,
            "appid": api_key
        }, timeout=5)
        
        if response.status_code == 200:
            data = response.json()
            return {
                "success": True,
                "city": data["name"],
                "temperature": data["main"]["temp"],
                "description": data["weather"][0]["description"],
                "humidity": data["main"]["humidity"],
                "wind_speed": data["wind"]["speed"]
            }
        else:
            return {
                "success": False,
                "error": f"API returned status code: {response.status_code}"
            }
            
    except requests.exceptions.Timeout:
        return {
            "success": False,
            "error": "API request timeout"
        }
    except Exception as e:
        return {
            "success": False,
            "error": str(e)
        }`,
        testInput: `{
  "city": "Hanoi",
  "units": "metric"
}`,
        expectedOutput: `{
  "success": true,
  "city": "Hanoi",
  "temperature": 28.5,
  "description": "partly cloudy",
  "humidity": 75,
  "wind_speed": 3.2
}`
    },

    errorHandling: {
        title: 'Error Handling',
        description: 'Function với error handling và validation',
        code: `@deploy
def validate_and_process(data: dict):
    """Process data with validation and error handling"""
    
    # Input validation
    required_fields = ["email", "age", "amount"]
    
    for field in required_fields:
        if field not in data:
            return {
                "success": False,
                "error": f"Missing required field: {field}",
                "error_type": "ValidationError"
            }
    
    # Email validation
    import re
    email_pattern = r'^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$'
    if not re.match(email_pattern, data["email"]):
        return {
            "success": False,
            "error": "Invalid email format",
            "error_type": "ValidationError"
        }
    
    # Age validation
    try:
        age = int(data["age"])
        if age < 0 or age > 150:
            raise ValueError("Age out of valid range")
    except (ValueError, TypeError):
        return {
            "success": False,
            "error": "Age must be a valid number between 0 and 150",
            "error_type": "ValidationError"
        }
    
    # Amount validation
    try:
        amount = float(data["amount"])
        if amount < 0:
            raise ValueError("Amount cannot be negative")
    except (ValueError, TypeError):
        return {
            "success": False,
            "error": "Amount must be a valid positive number",
            "error_type": "ValidationError"
        }
    
    # Process data
    try:
        # Simulate processing
        processed_data = {
            "email": data["email"].lower(),
            "age_group": "adult" if age >= 18 else "minor",
            "amount_with_tax": round(amount * 1.1, 2),
            "processing_timestamp": datetime.now().isoformat()
        }
        
        return {
            "success": True,
            "data": processed_data
        }
        
    except Exception as e:
        return {
            "success": False,
            "error": f"Processing error: {str(e)}",
            "error_type": "ProcessingError"
        }`,
        testInput: `{
  "email": "user@example.com",
  "age": "25",
  "amount": "100.50"
}`,
        expectedOutput: `{
  "success": true,
  "data": {
    "email": "user@example.com",
    "age_group": "adult",
    "amount_with_tax": 110.55,
    "processing_timestamp": "2024-07-17T10:30:00"
  }
}`
    }
};

const PythonTestExample = ({ selectedExample = 'basic' }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [copiedCode, setCopiedCode] = useState('');

    const example = pythonExamples[selectedExample];

    const handleCopy = (text, type) => {
        navigator.clipboard.writeText(text);
        setCopiedCode(type);
        setTimeout(() => setCopiedCode(''), 2000);
    };

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                    Dưới đây là các ví dụ về cách viết và test Python Lambda Functions. Copy code và test trong Jupyter notebook của bạn.
                </Typography>
            </Alert>

            <Paper variant="outlined">
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                        <Tab label="Python Code" icon={<IconBrandPython size={18} />} />
                        <Tab label="Test Input" icon={<IconTestPipe size={18} />} />
                        <Tab label="Expected Output" icon={<IconApi size={18} />} />
                    </Tabs>
                </Box>

                {activeTab === 0 && (
                    <Box sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6">{example.title}</Typography>
                            <Button size="small" startIcon={<IconCopy size={16} />} onClick={() => handleCopy(example.code, 'code')}>
                                {copiedCode === 'code' ? 'Copied!' : 'Copy Code'}
                            </Button>
                        </Box>
                        <Typography variant="body2" color="textSecondary" mb={2}>
                            {example.description}
                        </Typography>
                        <SyntaxHighlighter
                            language="python"
                            style={vscDarkPlus}
                            customStyle={{
                                margin: 0,
                                borderRadius: 4,
                                fontSize: '0.875rem'
                            }}
                        >
                            {example.code}
                        </SyntaxHighlighter>
                    </Box>
                )}

                {activeTab === 1 && (
                    <Box sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6">Test Input (JSON)</Typography>
                            <Button size="small" startIcon={<IconCopy size={16} />} onClick={() => handleCopy(example.testInput, 'input')}>
                                {copiedCode === 'input' ? 'Copied!' : 'Copy Input'}
                            </Button>
                        </Box>
                        <Typography variant="body2" color="textSecondary" mb={2}>
                            Input data để test function
                        </Typography>
                        <SyntaxHighlighter
                            language="json"
                            style={vscDarkPlus}
                            customStyle={{
                                margin: 0,
                                borderRadius: 4,
                                fontSize: '0.875rem'
                            }}
                        >
                            {example.testInput}
                        </SyntaxHighlighter>
                    </Box>
                )}

                {activeTab === 2 && (
                    <Box sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6">Expected Output</Typography>
                            <Chip label="Success Response" color="success" size="small" icon={<IconCircleCheck size={16} />} />
                        </Box>
                        <Typography variant="body2" color="textSecondary" mb={2}>
                            Kết quả mong đợi khi test thành công
                        </Typography>
                        <SyntaxHighlighter
                            language="json"
                            style={vscDarkPlus}
                            customStyle={{
                                margin: 0,
                                borderRadius: 4,
                                fontSize: '0.875rem'
                            }}
                        >
                            {example.expectedOutput}
                        </SyntaxHighlighter>
                    </Box>
                )}
            </Paper>

            <Card sx={{ mt: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Các bước test Python Function:
                    </Typography>
                    <Box component="ol" sx={{ pl: 2 }}>
                        <li>Copy Python code vào Jupyter notebook</li>
                        <li>Run cell để deploy function (decorator @deploy)</li>
                        <li>Vào Lambda Dashboard và click "Test" trên function card</li>
                        <li>Paste test input JSON vào Test Modal</li>
                        <li>Click "Run Test" để thực thi</li>
                        <li>Xem kết quả và logs trong Execution History</li>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" gutterBottom>
                        Tips for Testing:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                        <li>Test với nhiều test cases khác nhau (happy path, edge cases, error cases)</li>
                        <li>Kiểm tra performance metrics (execution time, memory usage)</li>
                        <li>Monitor error rate và success rate</li>
                        <li>
                            Sử dụng logging để debug: <code>logging.info()</code>
                        </li>
                        <li>Test với concurrent requests để kiểm tra scalability</li>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default PythonTestExample;
